import api from './api';

export const fetchStorefront = (companyCode) => {
  // Ensure companyCode is a string
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const url = `/storefront/${code}`;
  return api.get(url);
};

export const fetchStoreProduct = (companyCode, slug, params = {}) => {
  const code = String(companyCode || '').trim();
  const productSlug = String(slug || '').trim();
  if (!code || !productSlug) {
    return Promise.reject(new Error('Company code and product slug are required'));
  }
  const url = `/storefront/${code}/product/${encodeURIComponent(productSlug)}`;
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(url, { params: cleanParams });
};

export const fetchLegalPage = (companyCode, slug) => {
  const code = String(companyCode || '').trim();
  const pageSlug = String(slug || '').trim();
  if (!code || !pageSlug) {
    return Promise.reject(new Error('Company code and page slug are required'));
  }
  return api.get(`/storefront/${code}/legal/${encodeURIComponent(pageSlug)}`);
};

export const fetchStoreProducts = (companyCode, params = {}) => {
  // Ensure companyCode is a string
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const url = `/storefront/${code}/products`;
  
  // Filter out undefined values from params
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  
  return api.get(url, { params: cleanParams });
};

export const submitStoreCheckout = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/checkout`, payload);
};

/**
 * Start Kuveyt 3DS — returns HTML string for top-level document.write.
 * Never logs card data.
 */
export const initKuveytPayment = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/payments/kuveyt/init`, payload, {
    responseType: 'text',
    transformResponse: [(data) => data],
    headers: {
      Accept: 'text/html, application/json'
    }
  });
};

export const syncStorefrontCartSession = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/cart-sessions`, payload);
};

export const claimStorefrontCartSession = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/cart-sessions`, payload);
};

export const fetchStorefrontCartSession = (companyCode, reference) => {
  const code = String(companyCode || '').trim();
  const ref = String(reference || '').trim();
  if (!code || !ref) {
    return Promise.reject(new Error('Company code and cart reference are required'));
  }
  return api.get(`/storefront/${code}/cart-sessions/${encodeURIComponent(ref)}`);
};

export const expireStorefrontCartSession = (companyCode, reference) => {
  const code = String(companyCode || '').trim();
  const ref = String(reference || '').trim();
  if (!code || !ref) {
    return Promise.reject(new Error('Company code and cart reference are required'));
  }
  return api.delete(`/storefront/${code}/cart-sessions/${encodeURIComponent(ref)}`);
};

export const loginStorefrontMember = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/login`, payload);
};

export const registerStorefrontMember = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/register`, payload);
};

export const forgotStorefrontPassword = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/forgot-password`, payload);
};

export const resetStorefrontPassword = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/reset-password`, payload);
};

export const getStorefrontMemberProfile = (companyCode, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/profile`, { params: cleanParams });
};

export const updateStorefrontMemberProfile = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.put(`/storefront/${code}/profile`, payload);
};

export const getStorefrontMemberOrders = (companyCode, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/orders`, { params: cleanParams });
};

export const getStorefrontOrderDetail = (companyCode, orderId, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/orders/${orderId}`, { params: cleanParams });
};

export const getStorefrontOrderNotes = (companyCode, orderId, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/orders/${orderId}/notes`, { params: cleanParams });
};

export const addStorefrontOrderNote = (companyCode, orderId, payload, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.post(`/storefront/${code}/orders/${orderId}/notes`, { ...payload, ...cleanParams });
};

export const getStorefrontMemberQuotes = (companyCode, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/quotes`, { params: cleanParams });
};

export const addStorefrontAddress = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/profile/addresses`, payload);
};

export const updateStorefrontAddress = (companyCode, addressId, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.put(`/storefront/${code}/profile/addresses/${addressId}`, payload);
};

export const deleteStorefrontAddress = (companyCode, addressId, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.delete(`/storefront/${code}/profile/addresses/${addressId}`, { params: cleanParams });
};

export const getStorefrontQuoteDetail = (companyCode, quoteId, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/quotes/${quoteId}`, { params: cleanParams });
};

export const acceptStorefrontQuote = (companyCode, quoteId, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/quotes/${quoteId}/accept`, payload);
};

export const rejectStorefrontQuote = (companyCode, quoteId, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  return api.post(`/storefront/${code}/quotes/${quoteId}/reject`, payload);
};

export const downloadStorefrontQuotePDF = (companyCode, quoteId, params = {}) => {
  const code = String(companyCode || '').trim();
  if (!code) {
    return Promise.reject(new Error('Company code is required'));
  }
  const cleanParams = Object.keys(params).reduce((acc, key) => {
    if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
      acc[key] = params[key];
    }
    return acc;
  }, {});
  return api.get(`/storefront/${code}/quotes/${quoteId}/pdf`, { 
    params: cleanParams,
    responseType: 'blob'
  });
};

// Canlı destek sohbet
export const createChatRoom = (companyCode, payload) => {
  const code = String(companyCode || '').trim();
  if (!code) return Promise.reject(new Error('Company code is required'));
  return api.post(`/storefront/${code}/chat/rooms`, payload);
};

export const getChatRoomMessages = (companyCode, roomId) => {
  const code = String(companyCode || '').trim();
  if (!code) return Promise.reject(new Error('Company code is required'));
  return api.get(`/storefront/${code}/chat/rooms/${roomId}/messages`);
};

export const closeChatRoomByGuest = (companyCode, roomId) => {
  const code = String(companyCode || '').trim();
  if (!code) return Promise.reject(new Error('Company code is required'));
  return api.patch(`/storefront/${code}/chat/rooms/${roomId}/close`);
};

export const fetchStorefrontAbout = (companyCode) => {
  const code = String(companyCode || '').trim();
  if (!code) return Promise.reject(new Error('Company code is required'));
  return api.get(`/storefront/${code}/about`);
};

export const fetchStorefrontBlogPosts = (companyCode) => {
  const code = String(companyCode || '').trim();
  if (!code) return Promise.reject(new Error('Company code is required'));
  return api.get(`/storefront/${code}/blog`);
};

export const fetchStorefrontBlogPost = (companyCode, slug) => {
  const code = String(companyCode || '').trim();
  const postSlug = String(slug || '').trim();
  if (!code || !postSlug) return Promise.reject(new Error('Company code and slug are required'));
  return api.get(`/storefront/${code}/blog/${encodeURIComponent(postSlug)}`);
};

const cleanReviewParams = (params = {}) => Object.keys(params).reduce((acc, key) => {
  if (params[key] !== undefined && params[key] !== null && params[key] !== '') {
    acc[key] = params[key];
  }
  return acc;
}, {});

export const fetchProductReviews = (companyCode, productSlug, params = {}) => {
  const code = String(companyCode || '').trim();
  const slug = String(productSlug || '').trim();
  if (!code || !slug) {
    return Promise.reject(new Error('Company code and product slug are required'));
  }
  return api.get(`/storefront/${code}/product/${encodeURIComponent(slug)}/reviews`, {
    params: cleanReviewParams(params)
  });
};

export const fetchProductReviewEligibility = (companyCode, productSlug, params = {}) => {
  const code = String(companyCode || '').trim();
  const slug = String(productSlug || '').trim();
  if (!code || !slug) {
    return Promise.reject(new Error('Company code and product slug are required'));
  }
  return api.get(`/storefront/${code}/product/${encodeURIComponent(slug)}/reviews/eligibility`, {
    params: cleanReviewParams(params)
  });
};

export const submitProductReview = (companyCode, productSlug, payload) => {
  const code = String(companyCode || '').trim();
  const slug = String(productSlug || '').trim();
  if (!code || !slug) {
    return Promise.reject(new Error('Company code and product slug are required'));
  }
  return api.post(`/storefront/${code}/product/${encodeURIComponent(slug)}/reviews`, payload);
};
